import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  FiAlertCircle,
  FiCheck,
  FiGlobe,
  FiImage,
  FiLoader,
  FiSave,
  FiX,
} from 'react-icons/fi';
import { api } from '../services/api';
import { richTextToPlainText } from '../utils/richText';
import ProductRichTextEditor from './ProductRichTextEditor';
import './AdminProductDescriptionTranslation.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/avif'];

const resolveMediaUrl = (value) => (
  value && value.startsWith('/') ? `${API_URL}${value}` : value
);

const valueForLocale = (localeCode, items) => (
  (items || []).find((item) => item.locale === localeCode)?.description || ''
);

const emitSaveState = (productId, locale, state) => {
  window.dispatchEvent(new CustomEvent('haryad:translation-save-state', {
    detail: {
      entityType: 'product',
      entityId: String(productId),
      locale,
      fieldPath: 'description',
      state,
    },
  }));
};

const AdminProductDescriptionTranslation = ({ productId, sourceDescription = '', onClose }) => {
  const [loading, setLoading] = useState(true);
  const [locales, setLocales] = useState([]);
  const [translations, setTranslations] = useState([]);
  const [activeLocale, setActiveLocale] = useState('');
  const [value, setValue] = useState('');
  const [saveState, setSaveState] = useState('idle');
  const [switching, setSwitching] = useState(false);
  const [error, setError] = useState('');
  const saveTimers = useRef({});
  const pendingSaves = useRef({});
  const saveChains = useRef({});
  const requestNumbers = useRef({});
  const activeLocaleRef = useRef('');
  const mountedRef = useRef(true);

  const enabledLocales = useMemo(
    () => locales.filter((locale) => locale.enabled !== false && locale.code !== 'fa'),
    [locales]
  );
  const activeLanguage = enabledLocales.find((locale) => locale.code === activeLocale);
  const activeTranslation = translations.find((item) => item.locale === activeLocale);
  const sourceExcerpt = richTextToPlainText(sourceDescription).slice(0, 220);

  const updateLocalTranslation = useCallback((localeCode, description, extra = {}) => {
    setTranslations((items) => {
      const existing = items.find((item) => item.locale === localeCode);
      const updated = {
        ...(existing || { locale: localeCode, status: 'draft' }),
        description,
        hasUnpublishedChanges: true,
        ...extra,
      };
      return existing
        ? items.map((item) => (item === existing ? updated : item))
        : [...items, updated];
    });
  }, []);

  const persistPending = useCallback(async (localeCode) => {
    const pending = pendingSaves.current[localeCode];
    if (!pending) {
      try {
        await saveChains.current[localeCode];
        return true;
      } catch (_) {
        return false;
      }
    }

    delete pendingSaves.current[localeCode];
    if (activeLocaleRef.current === localeCode && mountedRef.current) setSaveState('saving');
    emitSaveState(productId, localeCode, 'saving');
    const previous = saveChains.current[localeCode] || Promise.resolve();
    const request = previous
      .catch(() => undefined)
      .then(() => api.patch(`/api/products/${productId}/translations/${localeCode}/field`, {
        path: 'description',
        value: pending.value,
      }));
    saveChains.current[localeCode] = request;

    try {
      const response = await request;
      emitSaveState(productId, localeCode, 'saved');
      if (!mountedRef.current) return true;
      updateLocalTranslation(localeCode, pending.value, {
        status: response.data?.status || 'draft',
        updatedAt: response.data?.updatedAt || new Date().toISOString(),
      });
      if (
        activeLocaleRef.current === localeCode
        && requestNumbers.current[localeCode] === pending.requestNumber
        && !pendingSaves.current[localeCode]
      ) {
        setSaveState('saved');
      }
      window.dispatchEvent(new CustomEvent('haryad:translations-changed', {
        detail: { entityType: 'product', entityId: productId },
      }));
      return true;
    } catch (saveError) {
      emitSaveState(productId, localeCode, 'error');
      if (mountedRef.current) {
        if (activeLocaleRef.current === localeCode) setSaveState('error');
        setError(saveError.data?.message || saveError.message || 'ذخیره پیش‌نویس توضیحات انجام نشد');
      }
      return false;
    }
  }, [productId, updateLocalTranslation]);

  const flushLocale = useCallback(async (localeCode) => {
    if (!localeCode) return true;
    if (saveTimers.current[localeCode]) {
      clearTimeout(saveTimers.current[localeCode]);
      delete saveTimers.current[localeCode];
    }
    return persistPending(localeCode);
  }, [persistPending]);

  const scheduleSave = (nextValue, localeCode) => {
    const requestNumber = (requestNumbers.current[localeCode] || 0) + 1;
    requestNumbers.current[localeCode] = requestNumber;
    pendingSaves.current[localeCode] = { value: nextValue, requestNumber };
    if (saveTimers.current[localeCode]) clearTimeout(saveTimers.current[localeCode]);
    setSaveState('waiting');
    emitSaveState(productId, localeCode, 'waiting');
    setError('');
    updateLocalTranslation(localeCode, nextValue);
    saveTimers.current[localeCode] = setTimeout(() => {
      delete saveTimers.current[localeCode];
      persistPending(localeCode);
    }, 700);
  };

  const selectLocale = async (localeCode) => {
    if (!localeCode || localeCode === activeLocaleRef.current || switching) return;
    setSwitching(true);
    const saved = await flushLocale(activeLocaleRef.current);
    if (!saved) {
      setSwitching(false);
      return;
    }
    activeLocaleRef.current = localeCode;
    setActiveLocale(localeCode);
    setValue(valueForLocale(localeCode, translations));
    setSaveState('idle');
    setError('');
    setSwitching(false);
  };

  const uploadLocalizedImage = async (file) => {
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      throw new Error('فرمت تصویر باید JPG، PNG، WEBP، GIF یا AVIF باشد.');
    }
    if (file.size > MAX_IMAGE_SIZE) {
      throw new Error('حجم تصویر نباید بیشتر از ۵ مگابایت باشد.');
    }
    if (!activeLocale) throw new Error('ابتدا زبان توضیحات را انتخاب کنید.');

    const formData = new FormData();
    formData.append('image', file);
    const response = await api.upload(
      `/api/products/${productId}/translations/${activeLocale}/description-images`,
      formData
    );
    const imageUrl = response.data?.imageUrl;
    if (!response.success || !imageUrl) throw new Error('آپلود تصویر انجام نشد.');
    return resolveMediaUrl(imageUrl);
  };

  const close = async () => {
    setSwitching(true);
    const saved = await flushLocale(activeLocaleRef.current);
    setSwitching(false);
    if (saved) onClose?.();
  };

  useEffect(() => {
    mountedRef.current = true;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const [localeResponse, translationResponse] = await Promise.all([
          api.get('/api/locales'),
          api.get(`/api/products/${productId}/translations`),
        ]);
        if (!mountedRef.current) return;
        const localeItems = Array.isArray(localeResponse.data) ? localeResponse.data : [];
        const translationItems = Array.isArray(translationResponse.data) ? translationResponse.data : [];
        const firstLocale = localeItems.find((item) => item.enabled !== false && item.code !== 'fa')?.code || '';
        setLocales(localeItems);
        setTranslations(translationItems);
        activeLocaleRef.current = firstLocale;
        setActiveLocale(firstLocale);
        setValue(valueForLocale(firstLocale, translationItems));
      } catch (loadError) {
        if (mountedRef.current) {
          setError(loadError.data?.message || loadError.message || 'دریافت توضیحات زبان‌ها انجام نشد');
        }
      } finally {
        if (mountedRef.current) setLoading(false);
      }
    };
    const timers = saveTimers.current;
    const pending = pendingSaves.current;
    load();
    return () => {
      mountedRef.current = false;
      Object.entries(timers).forEach(([localeCode, timer]) => {
        clearTimeout(timer);
        delete timers[localeCode];
      });
      Object.keys(pending).forEach((localeCode) => {
        persistPending(localeCode);
      });
    };
  }, [persistPending, productId]);

  return (
    <section className="admin-product-description-translation" aria-label="توضیحات چندزبانه محصول" dir="rtl">
      <header>
        <span className="admin-product-description-translation__icon"><FiGlobe /><FiImage /></span>
        <div>
          <h4>توضیحات کامل محصول در زبان‌های دیگر</h4>
          <p>متن، تصاویر، لینک‌ها و چینش هر زبان کاملاً مستقل ذخیره می‌شوند.</p>
        </div>
        <button type="button" className="ui-close-button" onClick={close} disabled={switching} aria-label="بستن ویرایشگر ترجمه">
          {switching ? <FiLoader /> : <FiX />}
        </button>
      </header>

      {loading ? (
        <div className="admin-product-description-translation__loading"><FiLoader /> در حال دریافت زبان‌ها و پیش‌نویس‌ها...</div>
      ) : (
        <>
          <div className="admin-product-description-translation__tabs" role="tablist" aria-label="انتخاب زبان توضیحات">
            {enabledLocales.map((locale) => {
              const translation = translations.find((item) => item.locale === locale.code);
              const filled = Boolean(richTextToPlainText(translation?.description) || /<img\b/i.test(translation?.description || ''));
              const stale = Boolean(translation?.isStale || translation?.isPublishedStale);
              return (
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeLocale === locale.code}
                  className={`${activeLocale === locale.code ? 'is-active' : ''} ${filled ? 'is-filled' : ''} ${stale ? 'is-stale' : ''}`}
                  key={locale.code}
                  onClick={() => selectLocale(locale.code)}
                  disabled={switching}
                >
                  <b>{locale.code.toUpperCase()}</b>
                  <span>{locale.nativeName}</span>
                  {filled ? <FiCheck /> : null}
                  {stale ? <small>نیاز به بازبینی</small> : null}
                </button>
              );
            })}
          </div>

          {activeLanguage ? (
            <div className="admin-product-description-translation__body">
              <div className="admin-product-description-translation__source">
                <FiAlertCircle />
                <span>
                  <strong>نسخهٔ فارسی به‌صورت خودکار کپی نمی‌شود.</strong>
                  <small>{sourceExcerpt ? `خلاصه متن فارسی: ${sourceExcerpt}${richTextToPlainText(sourceDescription).length > 220 ? '…' : ''}` : 'توضیحات فارسی هنوز خالی است.'}</small>
                </span>
              </div>

              <div className="admin-product-description-translation__editor-head">
                <div>
                  <strong>ویرایشگر {activeLanguage.nativeName}</strong>
                  <small>تصویر را در محل نشانگر تایپ آپلود کنید؛ فایل در پوشهٔ مستقل همین زبان قرار می‌گیرد.</small>
                </div>
                <span className={`is-${saveState}`}>
                  {saveState === 'idle' && (value ? 'آماده ویرایش' : 'پیش‌نویس خالی')}
                  {saveState === 'waiting' && 'در انتظار توقف تایپ...'}
                  {saveState === 'saving' && <><FiLoader /> در حال ذخیره پیش‌نویس...</>}
                  {saveState === 'saved' && <><FiCheck /> پیش‌نویس ذخیره شد</>}
                  {saveState === 'error' && 'ذخیره نشد'}
                </span>
              </div>

              <div dir={activeLanguage.direction || 'rtl'}>
                <ProductRichTextEditor
                  key={`${productId}:${activeLocale}`}
                  editorId={`product-description-${productId}-${activeLocale}`}
                  value={value}
                  onChange={(nextValue) => {
                    setValue(nextValue);
                    scheduleSave(nextValue, activeLocale);
                  }}
                  onUploadImage={uploadLocalizedImage}
                  placeholder={`توضیحات کامل ${activeLanguage.nativeName} را اینجا بنویسید...`}
                  ariaLabel={`توضیحات محصول به زبان ${activeLanguage.nativeName}`}
                  imageAlt={`تصویر محصول به زبان ${activeLanguage.nativeName}`}
                  direction={activeLanguage.direction || 'rtl'}
                />
              </div>

              <footer>
                <div>
                  <small>
                    {activeTranslation?.hasUnpublishedChanges
                      ? 'تغییرات در پیش‌نویس ذخیره می‌شوند؛ انتشار نهایی از دکمهٔ ثابت پایین فرم محصول انجام می‌شود.'
                      : activeTranslation?.status === 'published'
                        ? 'نسخهٔ منتشرشده این زبان به‌روز است.'
                        : 'پس از تکمیل تمام فیلدها، از دکمهٔ ثابت پایین فرم محصول استفاده کنید.'}
                  </small>
                  {error && <strong role="alert">{error}</strong>}
                </div>
                <div>
                  <button type="button" className={`is-save ${saveState === 'saving' ? 'is-loading' : ''}`} onClick={() => flushLocale(activeLocale)} disabled={['saving'].includes(saveState)}>
                    {saveState === 'saving' ? <FiLoader /> : <FiSave />} ذخیره پیش‌نویس
                  </button>
                </div>
              </footer>
            </div>
          ) : (
            <div className="admin-product-description-translation__empty">
              {error || 'زبان دیگری برای ترجمه فعال نشده است.'}
            </div>
          )}
        </>
      )}
    </section>
  );
};

export default AdminProductDescriptionTranslation;
